import type { Odds, PathEntry } from "./path.ts"
import type { Action } from "./world.ts"
import { cellValue, neighbours, WALL } from "./world.ts"

/**
 * The Odds on the reach: what the page draws of the Walker's instinct. A Path
 * entry carries its Odds keyed by Cell index; here they become one glow per
 * neighbouring Cell, and are held for exactly the window they mean "next".
 * Before a snare beat the glow leans toward the route; while the Walker
 * wanders it is even. Pure functions only.
 */

/** Every reachable Cell is marked, however unlikely. */
export const ODDS_FLOOR = 0.1

/** One neighbour's glow: the Action that lands there, and the opacity next to the Walker. */
export interface Glow {
  readonly cell: number
  readonly action: Action
  readonly peak: number
}

/**
 * The glow on each neighbour the Walker can enter from `cell`: its own chance,
 * floored. The direction is the Action's, so across the seam it runs the way
 * the Walker would, not the way the screen lies. A Wall gets nothing.
 */
export const glowOn = (grid: string, cell: number, odds: Odds): ReadonlyArray<Glow> =>
  neighbours(cell).flatMap(([action, n]) =>
    cellValue(grid, n) === WALL ? [] : [{ cell: n, action, peak: Math.max(ODDS_FLOOR, odds[String(n)] ?? 0) }]
  )

/**
 * The Odds held for Steps not yet heard, and the last Step the Walker landed
 * on. The glow for the window after landing on Step k is entry k + 1's Odds:
 * it switches on at that landing, or when the Odds arrive if later, and off at
 * the next landing. Odds for a Step already landed on are never kept.
 */
export interface Upcoming {
  readonly gen: number | undefined
  readonly landed: number | undefined
  readonly odds: ReadonlyMap<number, Odds>
}

export const emptyUpcoming: Upcoming = { gen: undefined, landed: undefined, odds: new Map() }

/** Hold the Odds of arriving Path entries. A Late Step's entry clears its Step: it has none. */
export const holdOdds = (upcoming: Upcoming, entries: ReadonlyArray<PathEntry>): Upcoming => {
  let { gen, landed } = upcoming
  let odds: Map<number, Odds> | undefined
  for (const entry of entries) {
    if (gen !== undefined && entry.gen < gen) continue
    if (entry.gen !== gen) {
      gen = entry.gen
      landed = undefined
      odds = new Map()
    }
    if (landed !== undefined && entry.step <= landed) continue
    odds ??= new Map(upcoming.odds)
    if (entry.odds === undefined) odds.delete(entry.step)
    else odds.set(entry.step, entry.odds)
  }
  return odds === undefined ? upcoming : { gen, landed, odds }
}

/** The Walker has landed on Step `step`: the old glow is off, and nothing at or before it is kept. */
export const landOn = (upcoming: Upcoming, gen: number, step: number): Upcoming => {
  if (upcoming.gen !== undefined && gen < upcoming.gen) return upcoming
  const odds = new Map<number, Odds>()
  if (gen === upcoming.gen) for (const [s, o] of upcoming.odds) if (s > step) odds.set(s, o)
  return { gen, landed: step, odds }
}

/** The Odds glowing now, if any: those of the Step after the one landed on. */
export const nextOdds = (upcoming: Upcoming): Odds | undefined =>
  upcoming.landed === undefined ? undefined : upcoming.odds.get(upcoming.landed + 1)

/** How many heard Steps the trail keeps behind the Walker: three Bars. */
export const TRAIL_STEPS = 48

/**
 * The trail after the Walker leaves `cell` for its next Step, newest last. A
 * Late Step leaves the Cell it stays on, so staying put shows as a repeat.
 */
export const leave = (trail: ReadonlyArray<number>, cell: number): ReadonlyArray<number> => [
  ...trail.slice(-(TRAIL_STEPS - 1)),
  cell
]
